import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import * as Icons from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import ImageGallery from "./ImageGallery";
import { pickLocale, telHref, mailHref, mapsUrl } from "@/lib/helpers";
import { track } from "@/api/analytics";
import { resolveImageUrl } from "@/components/ImageUploader";
import { toast } from "sonner";

/* --------------------------------------------------------------
 * "Today" weekday matcher used by the plain-text block renderer to
 * highlight a line that starts with the current localized weekday
 * (e.g. Lunedì / Mon / Mo). Only the visual line is styled — content
 * stays identical, so the CMS editor keeps working the same way.
 * -------------------------------------------------------------- */
const WEEKDAY_PREFIXES = {
  it: ["lunedì", "martedì", "mercoledì", "giovedì", "venerdì", "sabato", "domenica"],
  en: ["mon", "tue", "wed", "thu", "fri", "sat", "sun"],
  de: ["mo", "di", "mi", "do", "fr", "sa", "so"],
};
// JS getDay(): 0 = Sunday, 1 = Monday, ... 6 = Saturday
// Our arrays start on Monday, so we remap.
const jsToArrIdx = [6, 0, 1, 2, 3, 4, 5];

function todayPrefix(lang) {
  const arr = WEEKDAY_PREFIXES[lang] || WEEKDAY_PREFIXES.it;
  return arr[jsToArrIdx[new Date().getDay()]];
}

/** Render multi-line text, highlighting the line matching today's weekday. */
function TextWithTodayHighlight({ text, lang }) {
  if (!text) return null;
  const prefix = todayPrefix(lang);
  const lines = String(text).split("\n");
  return (
    <p className="text-ink-700 text-[15px] leading-relaxed">
      {lines.map((line, i) => {
        // Word-boundary match: prefix must be followed by a common day-name delimiter (":", "-", "–", " ", ",")
        // so short DE prefixes like "mo" don't wrongly match unrelated words ("motorino...", "montag stays fine").
        const trimmed = line.trim().toLowerCase();
        const isToday =
          trimmed.startsWith(prefix) &&
          /^[:\-–—\s,·.]/.test(trimmed.slice(prefix.length) || " ");
        return (
          <span key={i} className="block">
            {isToday ? (
              <span className="inline-flex items-center gap-2">
                <span className="inline-block w-1.5 h-1.5 rounded-full bg-bronze-400" />
                <strong className="text-bronze-500 font-semibold">{line}</strong>
              </span>
            ) : (
              line || <>&nbsp;</>
            )}
          </span>
        );
      })}
    </p>
  );
}

/** Copy-to-clipboard pill used inside the WiFi block. */
function CopyPill({ value, ariaLabel, testid }) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      toast.success(t("apartment.copied") || "Copiato");
      setTimeout(() => setCopied(false), 1400);
    } catch (e) {
      toast.error(t("common.error"));
    }
  };
  return (
    <button
      type="button"
      onClick={copy}
      aria-label={ariaLabel}
      data-testid={testid}
      className="inline-flex items-center gap-1.5 rounded-full border border-ink-200/50 bg-white px-2.5 py-1 text-[11px] font-semibold text-ink-700 hover:border-bronze-300 hover:text-bronze-500 active:scale-95 transition"
    >
      {copied ? <Icons.Check className="w-3 h-3 text-bronze-500" /> : <Icons.Copy className="w-3 h-3" />}
      {copied ? (t("apartment.copied") || "Copiato") : (t("apartment.copy") || "Copia")}
    </button>
  );
}

/** Dedicated Wi-Fi block: SSID + password + copy pills + optional QR overlay. */
function WifiBlock({ ssid, password, security, t }) {
  const [showQr, setShowQr] = useState(false);
  const sec = (security || "WPA").toUpperCase();
  const isOpen = sec === "NOPASS";
  // Standard WiFi QR schema recognized by iOS 11+, Android 10+ cameras.
  // T=WPA/WEP/nopass, S=ssid, P=password, H=hidden.
  const escape = (v) => String(v || "").replace(/([\\;,"':])/g, "\\$1");
  const secToken = isOpen ? "nopass" : sec === "WEP" ? "WEP" : "WPA";
  const qrPayload = `WIFI:T:${secToken};S:${escape(ssid)};P:${escape(password)};H:false;;`;
  // For open networks, the QR is meaningful with just the SSID.
  const canShowQr = ssid && (password || isOpen);
  return (
    <div
      className="rounded-2xl border border-ink-200/50 bg-cream-50/60 px-4 py-3.5"
      data-testid="block-wifi"
    >
      <div className="flex items-center justify-between gap-2 mb-3">
        <div className="flex items-center gap-2">
          <Icons.Wifi strokeWidth={1.4} className="w-4 h-4 text-bronze-500" />
          <span className="text-[10px] font-semibold uppercase tracking-[0.16em] text-bronze-500">Wi-Fi</span>
        </div>
        {canShowQr && (
          <button
            type="button"
            onClick={() => setShowQr((v) => !v)}
            data-testid="block-wifi-qr-toggle"
            aria-expanded={showQr}
            className="inline-flex items-center gap-1.5 rounded-full border border-ink-200/50 bg-white px-2.5 py-1 text-[11px] font-semibold text-ink-700 hover:border-bronze-300 hover:text-bronze-500 active:scale-95 transition"
          >
            <Icons.QrCode className="w-3 h-3" />
            {showQr ? (t("apartment.hide_qr") || "Nascondi QR") : (t("apartment.show_qr") || "QR")}
          </button>
        )}
      </div>
      {ssid && (
        <div className="flex items-center justify-between gap-3 py-2 border-b border-ink-200/30 last:border-0">
          <div className="min-w-0">
            <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-ink-300">
              {t("apartment.wifi_network") || "Rete"}
            </div>
            <div className="text-sm font-semibold text-ink-900 truncate">{ssid}</div>
          </div>
          <CopyPill value={ssid} ariaLabel={t("apartment.wifi_network") || "Rete"} testid="block-wifi-copy-ssid" />
        </div>
      )}
      {password && (
        <div className="flex items-center justify-between gap-3 py-2">
          <div className="min-w-0">
            <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-ink-300">
              {t("apartment.wifi_password") || "Password"}
            </div>
            <div className="text-sm font-semibold text-ink-900 truncate">{password}</div>
          </div>
          <CopyPill value={password} ariaLabel={t("apartment.wifi_password") || "Password"} testid="block-wifi-copy-pw" />
        </div>
      )}
      {showQr && canShowQr && (
        <div
          className="mt-3 pt-3 border-t border-ink-200/30 flex flex-col items-center"
          data-testid="block-wifi-qr"
        >
          <div className="p-3 bg-white rounded-xl border border-ink-200/40 shadow-soft">
            <QRCodeSVG value={qrPayload} size={168} level="M" bgColor="#FFFFFF" fgColor="#2A2622" />
          </div>
          <p className="text-[11px] text-ink-500 text-center mt-2 max-w-[240px]">
            {t("apartment.qr_hint") || "Inquadra il codice con la fotocamera per connetterti automaticamente"}
          </p>
        </div>
      )}
    </div>
  );
}

/** Renders a single CMS content block. */
function Block({ block }) {
  const { i18n, t } = useTranslation();
  const lang = i18n.language;
  const d = block.data || {};

  switch (block.type) {
    case "heading":
      return (
        <h3 className="font-editorial italic text-2xl text-ink-900 mt-2">
          {pickLocale(d.text, lang)}
        </h3>
      );
    case "text":
      return <TextWithTodayHighlight text={pickLocale(d.text, lang)} lang={lang} />;
    case "divider":
      return (
        <div className="flex justify-center my-3">
          <span className="ornament" />
        </div>
      );
    case "wifi": {
      const ssid = d.ssid || "";
      const password = d.password || "";
      const security = d.security || "WPA";
      if (!ssid && !password) return null;
      return <WifiBlock ssid={ssid} password={password} security={security} t={t} />;
    }
    case "checkin_out": {
      const inTime = d.checkin_time || "";
      const outTime = d.checkout_time || "";
      if (!inTime && !outTime) return null;
      const Cell = ({ icon: Icon, label, value, testid }) => (
        <div className="flex-1 rounded-2xl border border-ink-200/50 bg-white px-4 py-3.5" data-testid={testid}>
          <div className="flex items-center gap-2 mb-1.5">
            <Icon strokeWidth={1.4} className="w-3.5 h-3.5 text-bronze-500" />
            <span className="text-[10px] font-semibold uppercase tracking-[0.14em] text-bronze-500">
              {label}
            </span>
          </div>
          <div className="text-lg font-editorial italic text-ink-900 leading-none">{value}</div>
        </div>
      );
      return (
        <div className="flex gap-2.5">
          {inTime && (
            <Cell
              icon={Icons.LogIn}
              label={t("apartment.checkin") || "Check-in"}
              value={inTime}
              testid="block-checkin-in"
            />
          )}
          {outTime && (
            <Cell
              icon={Icons.LogOut}
              label={t("apartment.checkout") || "Check-out"}
              value={outTime}
              testid="block-checkin-out"
            />
          )}
        </div>
      );
    }
    case "image":
      return (
        <div className="rounded-2xl overflow-hidden bg-cream-100 my-2">
          <img src={resolveImageUrl(d.url)} alt={d.alt || ""} className="w-full h-auto object-cover" loading="lazy" />
          {d.caption && (
            <p className="px-3 py-2 text-xs text-ink-500 italic">{pickLocale(d.caption, lang)}</p>
          )}
        </div>
      );
    case "gallery":
      return <ImageGallery images={(d.images || []).map((i) => (typeof i === "string" ? i : i.url))} />;
    case "pdf":
      return (
        <a
          href={d.url}
          target="_blank"
          rel="noreferrer"
          className="flex items-center gap-3 rounded-xl border border-ink-200/50 bg-white px-4 py-3 hover:bg-cream-100 transition"
          data-testid="block-pdf"
        >
          <div className="w-9 h-9 rounded-lg bg-cream-100 flex items-center justify-center">
            <Icons.FileText strokeWidth={1.4} className="w-4 h-4 text-corten-400" />
          </div>
          <span className="flex-1 text-sm font-medium text-ink-900">{pickLocale(d.label, lang) || "PDF"}</span>
          <Icons.Download strokeWidth={1.4} className="w-4 h-4 text-ink-300" />
        </a>
      );
    case "youtube": {
      const vid = d.videoId;
      if (!vid) return null;
      // format: "horizontal" (16:9) | "vertical" (9:16, shorts)
      // size: "small" | "medium" | "large" | "full"
      const format = d.format === "vertical" ? "vertical" : "horizontal";
      const size = ["small", "medium", "large", "full"].includes(d.size) ? d.size : "full";
      const title = pickLocale(d.title, lang);
      const description = pickLocale(d.description, lang);
      // Width classes by size (centered inside the flex column).
      const widthClass = {
        small: "max-w-[240px]",
        medium: "max-w-[380px]",
        large: "max-w-[560px]",
        full: "max-w-full",
      }[size];
      const aspect = format === "vertical" ? "aspect-[9/16]" : "aspect-video";
      return (
        <div className="flex flex-col gap-2" data-testid="block-youtube">
          {title && (
            <h4 className="font-editorial italic text-xl text-ink-900">{title}</h4>
          )}
          <div className={`${widthClass} ${format === "vertical" ? "mx-auto" : "w-full"}`}>
            <div className={`rounded-2xl overflow-hidden bg-black/95 ${aspect}`}>
              <iframe
                title={title || "YouTube video"}
                src={`https://www.youtube.com/embed/${vid}`}
                className="w-full h-full"
                allowFullScreen
                loading="lazy"
              />
            </div>
          </div>
          {description && (
            <p className="text-ink-500 text-sm leading-relaxed">{description}</p>
          )}
        </div>
      );
    }
    case "button":
    case "link":
      return (
        <a
          href={d.url}
          target={d.url?.startsWith("http") ? "_blank" : undefined}
          rel="noreferrer"
          className={
            block.type === "button" || d.style === "primary"
              ? "inline-flex items-center justify-center gap-2 rounded-full bg-corten-400 text-white font-semibold px-5 py-3 shadow-md hover:bg-corten-500 active:scale-95 transition"
              : "inline-flex items-center gap-1.5 text-corten-500 font-medium underline underline-offset-4"
          }
          data-testid={`block-${block.type}`}
        >
          {pickLocale(d.label, lang) || d.url}
          <Icons.ArrowUpRight strokeWidth={1.6} className="w-4 h-4" />
        </a>
      );
    case "phone":
      return (
        <a
          href={telHref(d.number)}
          onClick={() => track("click_phone", { target_type: "block", target_id: block.id })}
          className="flex items-center gap-3 rounded-xl border border-ink-200/50 bg-white px-4 py-3 hover:bg-cream-100 transition"
          data-testid="block-phone"
        >
          <Icons.Phone strokeWidth={1.4} className="w-4 h-4 text-corten-400" />
          <div className="flex-1">
            <div className="text-xs text-ink-300 uppercase tracking-[0.12em]">{pickLocale(d.label, lang) || t("poi.call")}</div>
            <div className="text-sm font-semibold text-ink-900">{d.number}</div>
          </div>
        </a>
      );
    case "email":
      return (
        <a
          href={mailHref(d.email)}
          onClick={() => track("click_email", { target_type: "block", target_id: block.id })}
          className="flex items-center gap-3 rounded-xl border border-ink-200/50 bg-white px-4 py-3 hover:bg-cream-100 transition"
          data-testid="block-email"
        >
          <Icons.Mail strokeWidth={1.4} className="w-4 h-4 text-corten-400" />
          <div className="flex-1">
            <div className="text-xs text-ink-300 uppercase tracking-[0.12em]">{pickLocale(d.label, lang) || t("poi.email")}</div>
            <div className="text-sm font-semibold text-ink-900">{d.email}</div>
          </div>
        </a>
      );
    case "map": {
      const href = mapsUrl({ lat: d.lat, lng: d.lng, address: d.address });
      return href ? (
        <a
          href={href}
          target="_blank"
          rel="noreferrer"
          onClick={() => track("click_maps", { target_type: "block", target_id: block.id })}
          className="flex items-center gap-3 rounded-xl border border-ink-200/50 bg-white px-4 py-3 hover:bg-cream-100 transition"
          data-testid="block-map"
        >
          <Icons.MapPin strokeWidth={1.4} className="w-4 h-4 text-corten-400" />
          <div className="flex-1">
            <div className="text-xs text-ink-300 uppercase tracking-[0.12em]">{pickLocale(d.label, lang) || t("poi.directions")}</div>
            <div className="text-sm font-semibold text-ink-900">{d.address || `${d.lat}, ${d.lng}`}</div>
          </div>
          <Icons.ArrowUpRight strokeWidth={1.4} className="w-4 h-4 text-ink-300" />
        </a>
      ) : null;
    }
    default:
      return null;
  }
}

export default function ContentBlocks({ blocks = [] }) {
  if (!blocks?.length) return null;
  const ordered = [...blocks].sort((a, b) => (a.order || 0) - (b.order || 0));
  return (
    <div className="flex flex-col gap-3">
      {ordered.map((b, i) => {
        // Insert a hairline ornament between logical sections. A section starts
        // whenever a "heading" block appears (skip before the very first block).
        const showSeparator = i > 0 && b.type === "heading";
        return (
          <React.Fragment key={b.id}>
            {showSeparator && (
              <div className="flex justify-center py-1" aria-hidden>
                <span className="ornament opacity-70" />
              </div>
            )}
            <Block block={b} />
          </React.Fragment>
        );
      })}
    </div>
  );
}
